import React from 'react';

const WaitlistPage = ({ user, isDarkMode, onLogout }) => {
  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${
      isDarkMode ? 'bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-blue-50'
    }`}>
      <div className={`max-w-md w-full text-center p-8 rounded-2xl shadow-xl ${
        isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'
      }`}>
        {/* Logo/Icon */}
        <div className="mb-6">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
            isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'
          }`}>
            <svg className={`w-10 h-10 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {/* Welcome Message */}
        <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          You're on the Waitlist!
        </h1>
        
        <p className={`mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Thanks for signing up, <span className="font-semibold">@{user?.x_username || 'there'}</span>! 
          We're currently in private beta and will notify you when it's your turn.
        </p>

        {/* Status Card */}
        <div className={`p-4 rounded-xl mb-6 ${
          isDarkMode ? 'bg-slate-700/50 border border-slate-600' : 'bg-slate-50 border border-slate-200'
        }`}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
              Pending Approval
            </span>
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            We're reviewing your request and will send you an update soon.
          </p>
        </div>

        {/* What to Expect */}
        <div className={`text-left mb-6 p-4 rounded-xl ${
          isDarkMode ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-100'
        }`}>
          <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
            What to expect:
          </h3>
          <ul className={`text-sm space-y-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            <li className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Import and organize your X bookmarks</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>AI-powered investment research</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Daily digest emails with actionable insights</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <a 
            href="https://x.com/trytickler" 
            target="_blank" 
            rel="noopener noreferrer"
            className={`flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg font-medium transition-all ${
              isDarkMode 
                ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                : 'bg-slate-900 hover:bg-slate-800 text-white'
            }`}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Follow @trytickler for updates
          </a>
          
          <button
            onClick={onLogout}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
              isDarkMode 
                ? 'text-slate-400 hover:text-white hover:bg-slate-700' 
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            Sign out
          </button>
        </div>

        {/* Footer */}
        <p className={`mt-6 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Questions? Reach out to us on X
        </p>

        {/* Featured on TerminalValue.io badge */}
        <div className="mt-4 flex justify-center" data-testid="terminalvalue-badge-container">
          <a
            href="https://terminalvalue.io/tool/tickler"
            target="_blank"
            rel="noopener"
            data-testid="terminalvalue-badge-link"
          >
            <img
              src="https://terminalvalue.io/api/badges/tickler.svg?style=standard"
              alt="Featured on TerminalValue.io"
              width="230"
              height="54"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;
